import { useEffect, useMemo, useRef } from "react";
import { useAuthModule } from "@data-phone/react-resource-server-auth";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import { setSessionData } from "../redux/slices/session.slice";
import { useAccounts } from "../services/accounts.service";
import { useGetApps } from "services/apps.service";
import { useAccountChange } from "./useAccountChange";
import { useGetIntegrations } from "services/integrations.service";

export function useInitialLoader() {
  const hasRunRef = useRef(false);
  const { data: session, logout } = useAuthModule();

  const { refetch: refetchApps } = useGetApps();
  const { refetch: refetchIntegrations } = useGetIntegrations();

  useAccountChange(true, refetchApps, refetchIntegrations);

  const dispatch = useDispatch<AppDispatch>();
  const sessionData = useSelector((state: RootState) => state.session);

  useEffect(() => {
    if (!hasRunRef.current && session?.sessionData) {
      dispatch(
        setSessionData({
          isAuthenticated: session.isAuthenticated,
          sessionData: {
            ...session.sessionData,
          },
        })
      );
      hasRunRef.current = true;
    }
  }, [session?.sessionData, dispatch]);


  const hasSubAccountPermission = useMemo(() => {
    return sessionData?.sessionData?.permissions?.accounts?.includes("read:subAccount") ?? false;
  }, [sessionData?.sessionData?.permissions]);
  
  const hasAdminPermission = useMemo(() => {
    return sessionData?.sessionData?.permissions?.accounts?.includes("read:any") ?? false;
  }, [sessionData?.sessionData?.permissions]);

  const accountsQuery = useAccounts(
    !hasAdminPermission ? session?.sessionData?.accountID : undefined,
    {
      enabled: !!sessionData?.sessionData && (hasAdminPermission || hasSubAccountPermission),
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
    }
  );

  const isReady = !!sessionData?.sessionData;

  return {
    isReady,
    session: sessionData,
    accounts: accountsQuery.data,
    accountsQuery,
    logout,
    hasAdminPermission
  };
}
